import { useTheme } from "@emotion/react";
import { TextField, Box } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { tokens } from "../../theme";
import { useEffect, useState } from "react";
import Loader from "../global/Loader";
import { pageSizeOptions, pageSize, page } from "../../utils/paginationConfig";
import { DataGrid } from "@mui/x-data-grid";
import { CustomGridToolbar } from "../../utils/dataGridCustom";
import { getStudentLoginLogoutReport } from "../../services/staff-services/centerService";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import { SetCurrentPageTopic } from "../../store/action/headerChange";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { getAllCenters } from "../../services/getCenterAttendanceDetails";
import { saveAs } from "file-saver";

const ReportStudentLogInLogout = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const dispatch = useDispatch();
  dispatch(SetCurrentPageTopic("Student Daily Login / Logout Report"));

  const userRole = localStorage.getItem("Role");
  const userCenterCode = localStorage.getItem("CenterCode");

  const [paginationModel, setPaginationModel] = useState({page: page, pageSize: pageSize});
  const [rowCount, setRowCount] = useState(0);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [centerCode, setCenterCode] = useState("");
  const [allCenters, setAllCenters] = useState([]);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [dateFormat, setDateFormat] = useState(dayjs().format("DD-MMM-YYYY"));

  const headerData = ["Name", "PC ID", "Login Time", "Logout Time"];

  const columns = [
    {
      field: "studentName",
      headerName: headerData[0],
      flex: 4,
      renderCell: (params) => (
        <Box
          sx={{
            textTransform: "uppercase",
            display: "flex",
            alignItems: "flex-start",
            height: "100%",
            pt: "8px",
          }}
        >
          {params.value}
        </Box>
      ),
    },
    {
      field: "pcCode",
      headerName: headerData[1],
      flex: 2,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            alignItems: "flex-start",
            height: "100%",
            pt: "8px",
          }}
        >
          {params.value.map((pcCode, index) => (
            <Box
              key={index}
              sx={{
                height: "20px",
                minHeight: "20px",
                display: "flex",
                alignItems: "flex-start",
              }}
            >
              {pcCode || "\u00A0"} {/* &nbsp; for empty values */}
            </Box>
          ))}
        </Box>
      ),
    },
    {
      field: "loginTime",
      headerName: headerData[2],
      flex: 1,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            alignItems: "flex-start",
            height: "100%",
            pt: "8px",
          }}
        >
          {params.value.map((loginTime, index) => (
            <Box
              key={index}
              sx={{
                height: "20px",
                minHeight: "20px",
                display: "flex",
                alignItems: "flex-start",
              }}
            >
              {loginTime ? loginTime.slice(11, 16) : "\u00A0"}{/* &nbsp; for empty values */}
            </Box>
          ))}
        </Box>
      ),
    },
    {
      field: "logoutTime",
      headerName: headerData[3],
      flex: 1,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            alignItems: "flex-start",
            height: "100%",
            pt: "8px",
          }}
        >
          {params.value.map((logoutTime, index) => (
            <Box
              key={index}
              sx={{
                height: "20px",
                minHeight: "20px",
                display: "flex",
                alignItems: "flex-start",
              }}
            >
              {logoutTime ? logoutTime.slice(11, 16) : "\u00A0"}{/* &nbsp; for empty values */}
            </Box>
          ))}
        </Box>
      ),
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      const allCentersListResponse = await getAllCenters();

      if (userRole === "ADM" || userRole === "DPS" || userRole === "hmw") {
        const firstCenter = allCentersListResponse.data[0];
        setAllCenters(allCentersListResponse.data);
        setCenterCode(firstCenter?.centerCode);
        setSelectedCenter(firstCenter);
        await handleTableData(paginationModel.page, paginationModel.pageSize, firstCenter?.centerCode, dateFormat);
      } else {
        const filteredCenters = allCentersListResponse.data.filter(
          (center) => center.centerCode === userCenterCode
        );

        if (filteredCenters.length > 0) {
          const firstCenter = filteredCenters[0];
          setAllCenters(filteredCenters);
          setCenterCode(firstCenter?.centerCode);
          setSelectedCenter(firstCenter);
          await handleTableData(paginationModel.page, paginationModel.pageSize, firstCenter?.centerCode, dateFormat);
        }
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      const selectedDateString = selectedDate.$d;
      if (selectedDateString != "Invalid Date") {
        const dateObject = new Date(selectedDateString);
        const month = dateObject.toLocaleString("default", {
          month: "short",
        });
        const day = dateObject.getDate();
        const year = dateObject.getFullYear();

        const dateFormatted = `${day}-${month}-${year}`;
        setDateFormat(dateFormatted);
        handleTableData(page, pageSize, centerCode, dateFormatted);
      }
    } else {
      setSelectedDate(dayjs());
    }
  }, [selectedDate]);

  const handleTableData = async (page = paginationModel.page, limit = paginationModel.pageSize, code = centerCode, date = "") => {
    setIsLoading(true);

    setPaginationModel({page: page, pageSize: limit});

    try {
      const obj = {
        page,
        limit,
        centerCode: code,
        date: date,
      };

      const rows = [];
      const response = await getStudentLoginLogoutReport(obj);
      setRowCount(response.data.totalCount ?? 0);
      const apiData = response.data.data;

      apiData.forEach((student) => {
        const rowPCs = [];
        const rowLoginTimes = [];
        const rowLogoutTimes = [];
        student.studentPCs.forEach((pc) => {
          pc.sessions.forEach((session, sessionIndex) => {
            if(sessionIndex === 0){
                rowPCs.push(pc.pc_code)
            }else{
                rowPCs.push("");
            }

            rowLoginTimes.push(session.login_time);
            rowLogoutTimes.push(session.logout_time)
          });
        });

        rows.push({
            id: student.studentCode,
            studentCode: student.studentCode,
            studentName: student.studentName,
            pcCode: rowPCs,
            loginTime:rowLoginTimes,
            logoutTime: rowLogoutTimes,
        });

      });

      setData(rows);
    } catch (error) {
      console.log(error);
      toast.error("Please refresh");
    }

    setIsLoading(false);
  };

  const handleSelectCenter = (selectedOption) => {
    setSelectedCenter(selectedOption);

    if (selectedOption) {
      setCenterCode(selectedOption.centerCode);
      handleTableData(page, pageSize, selectedOption.centerCode, dateFormat);
    }
  };

  const handlePaginationModelChange = (newModel) => {
     const resetPage = newModel.pageSize !== paginationModel.pageSize;
     const updatedModel = {
       ...newModel,
       page: resetPage ? 0 : newModel.page,
     };
  
     setPaginationModel(updatedModel);
     handleTableData(updatedModel.page, updatedModel.pageSize, centerCode, dateFormat);
   };

   const handleExportCSV = async () => {
     try {
       const response = await getStudentLoginLogoutReport({
         page: 0,
         limit: rowCount,
         centerCode: centerCode,
         date: dateFormat,
       });
   
       const data = response.data.data;
       const csvRows = [];
       data.forEach((student) => {
        student.studentPCs.forEach((pc) => {
          pc.sessions.forEach((session, sessionIndex) => {

            csvRows.push([
              student.studentName,
              pc.pc_code,
              session.login_time,
              session.logout_time,
            ].join(","));
          });
        });
      });
   
       const csvContent = [headerData.join(","), ...csvRows].join("\n");
   
       const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
       saveAs(blob, `Student_login_logout_report.csv`);
     } catch (error) {
       console.error("CSV Export Failed:", error);
       toast.error("Failed to export CSV.");
     }
   
    };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Box m="0 20px">
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box sx={{ flex: 3, mt: "6px", mr: "10px" }}>
          <Autocomplete
            options={allCenters}
            getOptionLabel={(option) => option.centerName || ""}
            value={selectedCenter}
            onChange={(event, value) => handleSelectCenter(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Center"
                variant="filled"
                fullWidth
              />
            )}
          />
        </Box>
        <Box sx={{ flex: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Date"
              value={selectedDate}
              onChange={(newValue) => setSelectedDate(newValue)}
              disableFuture
              minDate={dayjs("2024-01-01")}
              maxDate={dayjs()}
              slotProps={{
                textField: {
                  variant: "filled",
                  fullWidth: true,
                },
              }}
            />
          </LocalizationProvider>
        </Box>
        <Box sx={{ flex: 4, mt: "6px", ml: "10px" }}></Box>
      </Box>

      <Box
        display="grid"
        width="100%"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
            fontSize: "15px",
          },
          "& .name-column--cell": {
            color: colors.primary[100],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            border: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.redAccent[100]} !important`,
          },
        }}
      >
        <DataGrid
          rows={data}
          getRowId={(row) => row.studentCode}
          columns={columns}
          paginationMode="server"
          rowCount={rowCount}
          pageSizeOptions={pageSizeOptions}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          loading={isLoading}
          components={{ Toolbar: CustomGridToolbar }}
          componentsProps={{
            toolbar: {
              onExport: handleExportCSV,
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default ReportStudentLogInLogout;
